import {
  ApprovalUser,
  ClassificationTag,
  DealNameHedge,
  FundCompany,
  LegalEntity,
  UserInitiator,
} from '@/models/FundLevel';
import {
  TradeRequestApproval,
  TradeRequestExecuted,
  TradeRequestExecution,
  TradeRequestMismatch,
  TradeRequestPostExecution,
} from '@/models/FundLevel/TradeNotifications';

import { ActionType, createAction, getType } from 'typesafe-actions';

import * as tradeActions from './actions';
import { submitTransactionFilesActions } from './actions';
import { MODULE } from './constants';
import {
  AsyncState,
  CompletedState,
  DocumentsForAllTrades,
  FieldExclusionData,
  FixReferenceData,
  TradeNotificationState,
  WorkflowStats,
} from './types';

type State = TradeNotificationState & {
  uploadDataCompleted: { resetTableToggle: boolean };
};

type TradeNotificationActionType =
  | ActionType<typeof tradeActions>
  | ReturnType<typeof acknowledgeTableReset>;

const asyncInit = <T>(data: T): AsyncState<T> => ({
  data,
  loading: false,
  error: null,
});

const initialCompleted: CompletedState = {
  completed: false,
  error: null,
  loading: false,
  data: null,
};

const initialWorkflowStats: WorkflowStats = {
  trades_for_approval: 0,
  trades_for_execution: 0,
  trades_sent_for_execution: 0,
  trades_mismatched: 0,
  trades_executed_today: 0,
  trades_cancelled: 0,
};

export const acknowledgeTableReset = createAction(
  `${MODULE}/acknowledgeTableReset`,
)();

const initialState: State = {
  approval: asyncInit<TradeRequestApproval[]>([]),
  executable: asyncInit<TradeRequestExecution[]>([]),
  fixConnection: asyncInit<string>(''),
  postExecution: asyncInit<TradeRequestPostExecution[]>([]),
  cancelled: asyncInit<TradeRequestPostExecution[]>([]),
  mismatch: asyncInit<TradeRequestMismatch[]>([]),
  executed: asyncInit<TradeRequestExecuted[]>([]),
  fixReference: asyncInit<FixReferenceData[]>([]),
  upload: initialCompleted,
  tradeDocuments: asyncInit<any[]>([]),
  downloadForFund: initialCompleted,
  downloadExecutionReport: initialCompleted,
  downloadExecutedTradesReport: initialCompleted,
  downloadAcceptanceTradesReport: initialCompleted,
  bindTransactions: initialCompleted,
  fundCompanies: asyncInit<FundCompany[]>([]),
  classificationTags: asyncInit<ClassificationTag[]>([]),
  userInitiators: asyncInit<UserInitiator[]>([]),
  workflowStats: asyncInit<WorkflowStats>(initialWorkflowStats),
  approvalUsers: asyncInit<ApprovalUser[]>([]),
  legalEntities: asyncInit<LegalEntity[]>([]),
  uploadDataCompleted: { resetTableToggle: false },
  fieldExclusionList: asyncInit<FieldExclusionData>({
    field_exclusion_list: [],
  }),
  dealNameHedges: asyncInit<DealNameHedge[]>([]),
  filteredFundCompanies: asyncInit<FundCompany[]>([]),
  filteredClassificationTags: asyncInit<ClassificationTag[]>([]),
  tradesFiles: asyncInit<DocumentsForAllTrades[]>([]),
};

const reducer = (
  state: State = initialState,
  action: TradeNotificationActionType,
): State => {
  switch (action.type) {
    case getType(tradeActions.getFundCompanies.request):
      return {
        ...state,
        fundCompanies: { ...state.fundCompanies, loading: true, error: null },
      };
    case getType(tradeActions.getFundCompanies.success):
      return {
        ...state,
        fundCompanies: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getFundCompanies.failure):
      return {
        ...state,
        fundCompanies: { ...state.fundCompanies, loading: false },
      };

    case getType(tradeActions.getClassificationTags.request):
      return {
        ...state,
        classificationTags: {
          ...state.classificationTags,
          loading: true,
          error: null,
        },
      };
    case getType(tradeActions.getClassificationTags.success):
      return {
        ...state,
        classificationTags: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getClassificationTags.failure):
      return {
        ...state,
        classificationTags: { ...state.classificationTags, loading: false },
      };

    case getType(tradeActions.getUserInitiators.request):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: true,
          error: null,
        },
      };
    case getType(tradeActions.getUserInitiators.success):
      return {
        ...state,
        userInitiators: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getUserInitiators.failure):
      return {
        ...state,
        userInitiators: { ...state.userInitiators, loading: false },
      };

    case getType(tradeActions.getApprovalUsers.request):
      return {
        ...state,
        approvalUsers: {
          ...state.approvalUsers,
          loading: true,
          error: null,
        },
      };
    case getType(tradeActions.getApprovalUsers.success):
      return {
        ...state,
        approvalUsers: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getApprovalUsers.failure):
      return {
        ...state,
        approvalUsers: { ...state.approvalUsers, loading: false },
      };

    case getType(tradeActions.getLegalEntities.request):
      return {
        ...state,
        legalEntities: {
          ...state.legalEntities,
          loading: true,
          error: null,
        },
      };
    case getType(tradeActions.getLegalEntities.success):
      return {
        ...state,
        legalEntities: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getLegalEntities.failure):
      return {
        ...state,
        legalEntities: { ...state.legalEntities, loading: false },
      };

    case getType(tradeActions.getApprovalTradeRequests.request):
      return {
        ...state,
        approval: { ...state.approval, loading: true, error: null },
      };
    case getType(tradeActions.getApprovalTradeRequests.success):
      return {
        ...state,
        approval: { data: action.payload.data, loading: false, error: null },
      };
    case getType(tradeActions.getApprovalTradeRequests.failure):
      return {
        ...state,
        approval: { ...state.approval, loading: false },
      };

    case getType(tradeActions.getExecutableTradeRequests.request):
      return {
        ...state,
        executable: { ...state.executable, loading: true, error: null },
      };
    case getType(tradeActions.getExecutableTradeRequests.success):
      return {
        ...state,
        executable: { data: action.payload.data, loading: false, error: null },
      };
    case getType(tradeActions.getExecutableTradeRequests.failure):
      return {
        ...state,
        executable: { ...state.executable, loading: false },
      };

    case getType(tradeActions.getPostExecutionTradeRequests.request):
      return {
        ...state,
        postExecution: { ...state.postExecution, loading: true, error: null },
      };
    case getType(tradeActions.getPostExecutionTradeRequests.success):
      return {
        ...state,
        postExecution: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getPostExecutionTradeRequests.failure):
      return {
        ...state,
        postExecution: { ...state.postExecution, loading: false },
      };

    case getType(tradeActions.getCancelledTradeRequests.request):
      return {
        ...state,
        cancelled: { ...state.cancelled, loading: true, error: null },
      };
    case getType(tradeActions.getCancelledTradeRequests.success):
      return {
        ...state,
        cancelled: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getCancelledTradeRequests.failure):
      return {
        ...state,
        cancelled: { ...state.cancelled, loading: false },
      };

    case getType(tradeActions.getExecutedDataMismatchTradeRequests.request):
      return {
        ...state,
        mismatch: { ...state.mismatch, loading: true, error: null },
      };
    case getType(tradeActions.getExecutedDataMismatchTradeRequests.success):
      return {
        ...state,
        mismatch: { data: action.payload.data, loading: false, error: null },
      };
    case getType(tradeActions.getExecutedDataMismatchTradeRequests.failure):
      return {
        ...state,
        mismatch: { ...state.mismatch, loading: false },
      };

    case getType(tradeActions.getExecutedTradeRequests.request):
      return {
        ...state,
        executed: { ...state.executed, loading: true, error: null },
      };
    case getType(tradeActions.getExecutedTradeRequests.success):
      return {
        ...state,
        executed: { data: action.payload.data, loading: false, error: null },
      };
    case getType(tradeActions.getExecutedTradeRequests.failure):
      return {
        ...state,
        executed: { ...state.executed, loading: false },
      };

    case getType(tradeActions.getWorkflowStats.request):
      return {
        ...state,
        workflowStats: { ...state.workflowStats, loading: true, error: null },
      };
    case getType(tradeActions.getWorkflowStats.success):
      return {
        ...state,
        workflowStats: {
          data: action.payload.data ?? state.workflowStats.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getWorkflowStats.failure):
      return {
        ...state,
        workflowStats: { ...state.workflowStats, loading: false },
      };

    case getType(tradeActions.getFixReferenceData.request):
      return {
        ...state,
        fixReference: { ...state.fixReference, loading: true, error: null },
      };
    case getType(tradeActions.getFixReferenceData.success):
      return {
        ...state,
        fixReference: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getFixReferenceData.failure):
      return {
        ...state,
        fixReference: { ...state.fixReference, loading: false },
      };

    case getType(tradeActions.checkFixConnectionActions.request):
      return {
        ...state,
        fixConnection: { ...state.fixConnection, loading: true, error: null },
      };
    case getType(tradeActions.checkFixConnectionActions.success):
      return {
        ...state,
        fixConnection: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.checkFixConnectionActions.failure):
      return {
        ...state,
        fixConnection: {
          data: '',
          error: action.payload?.error,
          loading: false,
        },
      };

    case getType(tradeActions.uploadTradeFile.request):
      return {
        ...state,
        upload: { loading: true, completed: false, error: null },
      };
    case getType(tradeActions.uploadTradeFile.success):
      return {
        ...state,
        upload: {
          loading: false,
          completed: true,
          error: null,
          data: action.payload.data,
        },
      };
    case getType(tradeActions.uploadTradeFile.failure):
      return {
        ...state,
        upload: {
          loading: false,
          completed: false,
          error: action.payload?.error ?? null,
        },
      };
    case getType(tradeActions.resetUpload):
      return { ...state, upload: { ...initialCompleted } };

    case getType(tradeActions.resetDownload):
      return { ...state, downloadForFund: { ...initialCompleted } };

    case getType(submitTransactionFilesActions.request):
      return {
        ...state,
        bindTransactions: { loading: true, completed: false, error: null },
      };

    case getType(submitTransactionFilesActions.success):
      return {
        ...state,
        bindTransactions: { loading: false, completed: true, error: null },
      };

    case getType(submitTransactionFilesActions.failure):
      return {
        ...state,
        bindTransactions: {
          loading: false,
          completed: false,
          error: action.payload?.error ?? 'Bind transactions failed',
        },
      };

    case getType(tradeActions.fetchDocumentsForTradeActions.request):
      return {
        ...state,
        tradeDocuments: { data: [], loading: true, error: null },
      };
    case getType(tradeActions.fetchDocumentsForTradeActions.success):
      return {
        ...state,
        tradeDocuments: {
          loading: false,
          data: action.payload.data,
          error: null,
        },
      };
    case getType(tradeActions.fetchDocumentsForTradeActions.failure):
      return {
        ...state,
        tradeDocuments: {
          ...state.tradeDocuments,
          loading: false,
          error: action.payload?.error ?? 'Error fetching Documents for Trade',
        },
      };

    case getType(tradeActions.downloadTemplate.request):
      return {
        ...state,
        downloadForFund: { loading: true, completed: false, error: null },
      };
    case getType(tradeActions.downloadTemplate.success):
      return {
        ...state,
        downloadForFund: { loading: false, completed: true, error: null },
      };
    case getType(tradeActions.downloadTemplate.failure):
      return {
        ...state,
        downloadForFund: {
          loading: false,
          completed: false,
          error: action.payload?.error ?? null,
        },
      };

    //execution report
    case getType(tradeActions.downloadExecutionReport.request):
      return {
        ...state,
        downloadExecutionReport: {
          loading: true,
          completed: false,
          error: null,
        },
      };
    case getType(tradeActions.downloadExecutionReport.success):
      return {
        ...state,
        downloadExecutionReport: {
          loading: false,
          completed: true,
          error: null,
        },
      };
    case getType(tradeActions.downloadExecutionReport.failure):
      return {
        ...state,
        downloadExecutionReport: {
          loading: false,
          completed: false,
          error: action.payload?.error ?? null,
        },
      };

    case getType(tradeActions.downloadExecutedTradesReport.request):
      return {
        ...state,
        downloadExecutedTradesReport: {
          loading: true,
          completed: false,
          error: null,
        },
      };
    case getType(tradeActions.downloadExecutedTradesReport.success):
      return {
        ...state,
        downloadExecutedTradesReport: {
          loading: false,
          completed: true,
          error: null,
        },
      };
    case getType(tradeActions.downloadExecutedTradesReport.failure):
      return {
        ...state,
        downloadExecutedTradesReport: {
          loading: false,
          completed: false,
          error: action.payload?.error ?? null,
        },
      };

    case getType(tradeActions.downloadTradesforAcceptanceReport.request):
      return {
        ...state,
        downloadAcceptanceTradesReport: {
          loading: true,
          completed: false,
          error: null,
        },
      };
    case getType(tradeActions.downloadTradesforAcceptanceReport.success):
      return {
        ...state,
        downloadAcceptanceTradesReport: {
          loading: false,
          completed: true,
          error: null,
        },
      };
    case getType(tradeActions.downloadTradesforAcceptanceReport.failure):
      return {
        ...state,
        downloadAcceptanceTradesReport: {
          loading: false,
          completed: false,
          error: action.payload?.error ?? null,
        },
      };
    case getType(tradeActions.createTradeNotificationActions.request):
      return {
        ...state,
        approval: { ...state.approval, loading: true, error: null },
        uploadDataCompleted: { resetTableToggle: false },
      };
    case getType(tradeActions.createTradeNotificationActions.success):
      return {
        ...state,
        approval: { ...state.approval, loading: false, error: null },
        uploadDataCompleted: { resetTableToggle: true },
      };
    case getType(tradeActions.createTradeNotificationActions.failure):
      return {
        ...state,
        approval: { ...state.approval, loading: false },
        uploadDataCompleted: { resetTableToggle: false },
      };
    case getType(acknowledgeTableReset):
      return {
        ...state,
        uploadDataCompleted: { resetTableToggle: false },
      };

    case getType(tradeActions.insertSentForExecutionStatusActions.request):
      return {
        ...state,
        approval: { ...state.approval, loading: true, error: null },
      };
    case getType(tradeActions.insertSentForExecutionStatusActions.success):
      return {
        ...state,
        approval: { ...state.approval, loading: false, error: null },
      };
    case getType(tradeActions.insertSentForExecutionStatusActions.failure):
      return {
        ...state,
        approval: { ...state.approval, loading: false },
      };

    case getType(tradeActions.insertClientApprovedStatusActions.request):
      return {
        ...state,
        approval: { ...state.approval, loading: true, error: null },
      };
    case getType(tradeActions.insertClientApprovedStatusActions.success):
      return {
        ...state,
        approval: { ...state.approval, loading: false, error: null },
      };
    case getType(tradeActions.insertClientApprovedStatusActions.failure):
      return {
        ...state,
        approval: { ...state.approval, loading: false },
      };

    case getType(tradeActions.insertTradeRequestStatusActions.request):
      return {
        ...state,
        approval: { ...state.approval, loading: true, error: null },
      };
    case getType(tradeActions.insertTradeRequestStatusActions.success):
      return {
        ...state,
        approval: { ...state.approval, loading: false, error: null },
      };
    case getType(tradeActions.insertTradeRequestStatusActions.failure):
      return {
        ...state,
        approval: { ...state.approval, loading: false },
      };

    case getType(tradeActions.insertPMCRMApprovedStatusActions.request):
      return {
        ...state,
        approval: { ...state.approval, loading: true, error: null },
      };
    case getType(tradeActions.insertPMCRMApprovedStatusActions.success):
      return {
        ...state,
        approval: { ...state.approval, loading: false, error: null },
      };
    case getType(tradeActions.insertPMCRMApprovedStatusActions.failure):
      return {
        ...state,
        approval: { ...state.approval, loading: false },
      };

    case getType(tradeActions.updateTradeRequestsActions.request):
      return {
        ...state,
        approval: { ...state.approval, loading: true, error: null },
      };
    case getType(tradeActions.updateTradeRequestsActions.success):
      return {
        ...state,
        approval: { ...state.approval, loading: false, error: null },
      };
    case getType(tradeActions.updateTradeRequestsActions.failure):
      return {
        ...state,
        approval: { ...state.approval, loading: false },
      };

    case getType(tradeActions.approvedTradeRequestsGroupingActions.request):
      return {
        ...state,
        approval: { ...state.approval, loading: true, error: null },
      };
    case getType(tradeActions.approvedTradeRequestsGroupingActions.success):
      return {
        ...state,
        approval: { ...state.approval, loading: false, error: null },
      };
    case getType(tradeActions.approvedTradeRequestsGroupingActions.failure):
      return {
        ...state,
        approval: { ...state.approval, loading: false },
      };

    case getType(tradeActions.removeTradeRequestsGroupingActions.request):
      return {
        ...state,
        approval: { ...state.approval, loading: true, error: null },
      };
    case getType(tradeActions.removeTradeRequestsGroupingActions.success):
      return {
        ...state,
        approval: { ...state.approval, loading: false, error: null },
      };
    case getType(tradeActions.removeTradeRequestsGroupingActions.failure):
      return {
        ...state,
        approval: { ...state.approval, loading: false },
      };

    case getType(tradeActions.hideSelectedTradesActions.request):
      return {
        ...state,
        postExecution: { ...state.postExecution, loading: true, error: null },
        mismatch: { ...state.mismatch, loading: true, error: null },
      };
    case getType(tradeActions.hideSelectedTradesActions.success):
      return {
        ...state,
        postExecution: { ...state.postExecution, loading: false, error: null },
        mismatch: { ...state.mismatch, loading: false, error: null },
      };
    case getType(tradeActions.hideSelectedTradesActions.failure):
      return {
        ...state,
        postExecution: { ...state.postExecution, loading: false },
        mismatch: { ...state.mismatch, loading: false },
      };

    case getType(tradeActions.fetchFieldExclusionsActions.request):
      return {
        ...state,
        fieldExclusionList: { ...state.fieldExclusionList, loading: true },
      };
    case getType(tradeActions.fetchFieldExclusionsActions.success):
      return {
        ...state,
        fieldExclusionList: {
          ...state.fieldExclusionList,
          loading: false,
          data: action.payload.data,
        },
      };
    case getType(tradeActions.fetchFieldExclusionsActions.failure):
      return {
        ...state,
        fieldExclusionList: {
          ...state.fieldExclusionList,
          loading: false,
          error: action.payload?.error ?? 'Error fetching Field Exclusions',
        },
      };

    case getType(tradeActions.createUserInitiatorActions.request):
      return {
        ...state,
        userInitiators: {
          ...state.userInitiators,
          loading: true,
          error: null,
        },
      };
    case getType(tradeActions.createUserInitiatorActions.success):
      return {
        ...state,
        userInitiators: {
          data: [action.payload, ...state.userInitiators.data],
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.createUserInitiatorActions.failure):
      return {
        ...state,
        userInitiators: { ...state.userInitiators, loading: false },
      };

    case getType(tradeActions.updateTradeRequestStatusActions.request):
      return {
        ...state,
        approval: { ...state.approval, loading: true, error: null },
      };
    case getType(tradeActions.updateTradeRequestStatusActions.success):
      return {
        ...state,
        approval: { ...state.approval, loading: false, error: null },
      };
    case getType(tradeActions.updateTradeRequestStatusActions.failure):
      return {
        ...state,
        approval: { ...state.approval, loading: false },
      };

    case getType(tradeActions.getDealNameHedgeDataActions.request):
      return {
        ...state,
        dealNameHedges: { ...state.dealNameHedges, loading: true, error: null },
      };
    case getType(tradeActions.getDealNameHedgeDataActions.success):
      return {
        ...state,
        dealNameHedges: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getDealNameHedgeDataActions.failure):
      return {
        ...state,
        dealNameHedges: { ...state.dealNameHedges, loading: false },
      };
    case getType(tradeActions.getFileteredFundCompanies.request):
      return {
        ...state,
        filteredFundCompanies: {
          ...state.filteredFundCompanies,
          loading: true,
          error: null,
        },
      };
    case getType(tradeActions.getFileteredFundCompanies.success):
      return {
        ...state,
        filteredFundCompanies: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getFileteredFundCompanies.failure):
      return {
        ...state,
        filteredFundCompanies: {
          ...state.filteredFundCompanies,
          loading: false,
        },
      };

    case getType(tradeActions.getFilteredClassificationTags.request):
      return {
        ...state,
        filteredClassificationTags: {
          ...state.filteredClassificationTags,
          loading: true,
          error: null,
        },
      };
    case getType(tradeActions.getFilteredClassificationTags.success):
      return {
        ...state,
        filteredClassificationTags: {
          data: action.payload.data,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.getFilteredClassificationTags.failure):
      return {
        ...state,
        filteredClassificationTags: {
          ...state.filteredClassificationTags,
          loading: false,
        },
      };

    case getType(tradeActions.fetchDocumentsForAllTradeActions.request):
      return {
        ...state,
        tradesFiles: {
          ...state.tradesFiles,
          loading: true,
          error: null,
        },
      };
    case getType(tradeActions.fetchDocumentsForAllTradeActions.success):
      return {
        ...state,
        tradesFiles: {
          data: action.payload,
          loading: false,
          error: null,
        },
      };
    case getType(tradeActions.fetchDocumentsForAllTradeActions.failure):
      return {
        ...state,
        tradesFiles: {
          ...state.tradesFiles,
          loading: false,
        },
      };

    default:
      return state;
  }
};

export default reducer;
